import { motion } from "framer-motion";
import { Cctv, LockKeyhole, ListChecks, CheckCircle2 } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { useLang } from "../../i18n";

const CameraTile = ({ t }) => (
  <article data-testid="security-camera" className="relative overflow-hidden rounded-[28px] bg-[#1f1f1f] lg:row-span-2">
    <img src="/images/hero.jpg" alt={t.alt} loading="lazy" className="h-72 w-full object-cover opacity-80 grayscale sm:h-96 lg:absolute lg:inset-0 lg:h-full" />
    <div className="absolute inset-0 bg-[repeating-linear-gradient(0deg,rgba(0,0,0,0.12)_0px,rgba(0,0,0,0.12)_1px,transparent_1px,transparent_3px)]" />
    <span className="absolute left-5 top-5 flex items-center gap-2 rounded-full bg-[#1f1f1f]/70 px-3 py-1.5 text-xs font-bold uppercase tracking-widest text-white backdrop-blur">
      <span className="h-2 w-2 animate-pulse rounded-full bg-[#F26D5A]" /> {t.live}
    </span>
    {["left-4 top-14 border-l-2 border-t-2", "right-4 top-14 border-r-2 border-t-2", "left-4 bottom-28 border-b-2 border-l-2", "right-4 bottom-28 border-b-2 border-r-2"].map((c) => (
      <span key={c} className={`absolute h-8 w-8 border-[#F7D048] ${c}`} />
    ))}
    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#1f1f1f] via-[#1f1f1f]/80 to-transparent p-6 pt-16">
      <Cctv className="h-6 w-6 text-[#F7D048]" />
      <h3 className="font-display mt-3 text-2xl font-extrabold text-white">{t.t}</h3>
      <p className="mt-2 max-w-md text-sm text-[#D9D2C8]">{t.d}</p>
    </div>
  </article>
);

const LockTile = ({ t }) => (
  <article data-testid="security-lock" className="group relative overflow-hidden rounded-[28px] bg-[#F26D5A] p-7 text-[#1f1f1f]">
    <motion.div
      className="absolute -right-6 -top-6 grid h-36 w-36 place-items-center rounded-full border-2 border-[#1f1f1f]/20"
      animate={{ rotate: 360 }}
      transition={{ duration: 24, repeat: Infinity, ease: "linear" }}
    >
      <span className="h-3 w-3 -translate-y-[72px] rounded-full bg-[#F7D048]" />
    </motion.div>
    <LockKeyhole className="h-8 w-8 transition-transform duration-500 group-hover:-rotate-12" />
    <h3 className="font-display mt-10 text-2xl font-extrabold">{t.t}</h3>
    <p className="mt-2 text-sm font-medium text-[#2D2D2D]">{t.d}</p>
  </article>
);

const AuditTile = ({ t }) => (
  <article data-testid="security-audit" className="rounded-[28px] border border-[#A69B8F]/35 bg-white p-7">
    <ListChecks className="h-8 w-8 text-[#C9503F]" />
    <h3 className="font-display mt-6 text-2xl font-extrabold text-[#2D2D2D]">{t.t}</h3>
    <ul className="mt-5 space-y-2.5">
      {t.log.map((l, i) => (
        <motion.li
          key={l}
          initial={{ opacity: 0, x: -12 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ delay: 0.2 + i * 0.15 }}
          className="flex items-center gap-3 rounded-xl bg-[#F4F0E8] px-4 py-2.5 text-sm font-semibold text-[#2D2D2D]"
        >
          <CheckCircle2 className="h-4 w-4 shrink-0 text-[#2f8f4e]" /> {l}
        </motion.li>
      ))}
    </ul>
  </article>
);

export const Security = () => {
  const { t: all } = useLang();
  const t = all.security;
  return (
  <section id="seguretat" data-testid="security-section" className="bg-[#FDFBF7] pb-24 sm:pb-32">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal className="max-w-2xl">
        <Eyebrow>{t.eyebrow}</Eyebrow>
        <h2 className="font-display mt-5 text-4xl font-extrabold leading-[1] tracking-tight text-[#2D2D2D] sm:text-5xl">
          {t.title[0]} <span className="italic text-[#F26D5A]">{t.title[1]}</span>
        </h2>
        <p className="mt-5 text-base text-[#5a524a] md:text-lg">
          {t.intro}
        </p>
      </Reveal>
      <div className="mt-14 grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Reveal className="h-full"><div className="h-full lg:min-h-[620px] [&>article]:h-full"><CameraTile t={t.cam} /></div></Reveal>
        <div className="flex flex-col gap-5">
          <Reveal delay={0.1} className="flex-1 [&>article]:h-full"><LockTile t={t.lock} /></Reveal>
          <Reveal delay={0.2} className="flex-1 [&>article]:h-full"><AuditTile t={t.audit} /></Reveal>
        </div>
      </div>
    </div>
  </section>
  );
};
